import * as React from "react";
import { ErrorBoundary } from "@appsignal/react";
import {
  Editor,
  getEventTransfer,
  OnChangeParam,
  RenderInlineProps,
  RenderBlockProps,
} from "slate-react";
import {
  Editor as SlateEditor,
  Value,
  RangeProperties,
  Controller,
} from "slate";
import {
  displayURL,
  getVimeoVideoId,
  getYoutubeVideoId,
  isImage,
  isUrl,
} from "@features/utils/functions";
import { newLineToParagraph } from "@features/utils/reactFunctions";
import { IGetStringKey } from "@appInterfaces/general.types";
import ITextEditor, { IEditorImageVideo, IEditorLink } from "./ITextEditor";
import BttfAxios from "@features/utils/axios";
import Loader from "@appComponents/Loader/Loader";

import { appsignal } from "./../../../../appsignal";
import * as style from "./TextEditorStyle.module.scss";
import * as elemsStyle from "@appStyles/_elements.module.scss";
type Any = any;

const Image = React.forwardRef(
  (props: IEditorImageVideo, ref: React.Ref<HTMLImageElement>) => {
    const style = {
      display: "block",
      maxWidth: "100%",
      boxShadow: props.selected ? "0 0 0 2px #81109c" : "none",
    };

    return <img {...props} style={style} />;
  }
);

const Video = React.forwardRef(
  (props: IEditorImageVideo, ref: React.Ref<HTMLVideoElement>) => {
    const style = {
      boxShadow: props.selected ? "0 0 0 2px #81109c" : "none",
      backgroundColor: "black",
      maxWidth: "100%",
      width: "100%",
    };

    return <iframe height="427" frameBorder="0" {...props} style={style} />;
  }
);

const Link = React.forwardRef(
  (props: IEditorLink, ref: React.Ref<HTMLLinkElement>) => {
    const style: IGetStringKey = {
      color: "#81109c",
      fontWeight: "bold",
      textDecoration: "underline",
    };

    return (
      <a
        {...props}
        href={props.href}
        style={style}
        rel="noopener noreferrer"
        target="_blank"
        contentEditable={false}
      >
        {props.children}
      </a>
    );
  }
);

const uploadImageSizeLimit = 5;

const PLACEHOLDER_TEXT = "Say something ...";

const initialValue = Value.fromJSON({
  document: {
    nodes: [
      {
        object: "block",
        type: "paragraph",
        nodes: [
          {
            object: "text",
            text: PLACEHOLDER_TEXT,
          },
        ],
      },
    ],
  },
}) as Any;

const allowedImageTypes = ["image/jpeg", "image/png"];

export type TState = {
  value: Any;
  isFocused: boolean;
  loading: boolean;
  isEmpty: boolean;
  imagesId: { id: string; name: string }[];
};

export default class TextEditor extends React.Component<ITextEditor, TState> {
  inputFile: HTMLInputElement;
  withoutEditor: boolean = false;
  private editorRef: React.RefObject<Editor>;

  constructor(props: ITextEditor) {
    super(props);
    this.editorRef = React.createRef();
    let value = initialValue;

    if (this.props.read) {
      try {
        value = Value.fromJSON(JSON.parse(this.props.read));
      } catch {
        this.withoutEditor = true;
      }
    }

    this.state = {
      value,
      isFocused: false,
      loading: false,
      isEmpty: true,
      imagesId: [],
    };
  }

  sendValueToParent = () => {
    const valueJSON = this.state.value.toJSON();
    this.setState({ value: initialValue });

    const imagesId: string[] = [];
    valueJSON.document.nodes.filter(
      (elem: { type: string; data: { src: string } }) => {
        return (
          elem.type === "image" &&
          this.state.imagesId.some((e) => {
            if (e.name === elem.data.src) {
              imagesId.push(e.id);
              return true;
            }

            return false;
          })
        );
      }
    );

    return {
      imagesId,
      message: JSON.stringify(valueJSON),
    };
  };

  onFocus = (event: Event) => {
    const val = this.state.value.toJSON().document!.nodes;
    const isEmpty = this.displayedValueEquals(val, PLACEHOLDER_TEXT);

    if (isEmpty) {
      this.editorRef.current?.deleteLineForward();
      this.editorRef.current?.deleteLineBackward();
      this.editorRef.current?.focus();
    }
  };

  onBlur = () => {
    const val = this.state.value.toJSON().document!.nodes;
    const isEmpty = this.displayedValueEquals(val);
    if (isEmpty) {
      this.editorRef.current?.insertText(PLACEHOLDER_TEXT);
      this.editorRef.current?.blur();
    }
  };

  onChange = ({ value }: OnChangeParam) => {
    const val = value.toJSON().document!.nodes;

    if (this.props.read) {
      return;
    }

    if (val && val.length === 0) {
      this.setEditorEmpty();
      if (this.props.setButtonStatus !== null) {
        this.props.setButtonStatus(false);
      }
      return;
    }

    const isEmpty = this.displayedValueEquals(val);
    const isFocused = value.selection.isFocused;

    this.setState({ value, isEmpty });

    // without setTimeout we need to double click on action buttons when editor is focused
    setTimeout(() => {
      this.setState({ isFocused });
    }, 100);

    if (this.props.setButtonStatus !== null) {
      this.props.setButtonStatus(!isEmpty);
    }
  };

  onImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const reader: FileReader = new FileReader();
    const file = event.target.files ? event.target.files[0] : null;

    if (file && file.size > uploadImageSizeLimit * 1024 * 1024) {
      alert(
        `The file size can't exceed ${uploadImageSizeLimit}MB. Please upload smaller image.`
      );
    } else if (file) {
      reader.readAsDataURL(file);

      const val = this.state.value.toJSON().document!.nodes;
      const isEmpty = this.displayedValueEquals(val, PLACEHOLDER_TEXT);
      if (isEmpty) {
        this.editorRef.current?.deleteLineForward();
        this.editorRef.current?.deleteLineBackward();
        this.editorRef.current?.focus();
      }

      reader.onloadend = () => {
        if (allowedImageTypes.includes(file.type)) {
          this.uploadImage(reader.result).then((imageURL) => {
            this.editorRef.current!.command(this.insertImage, imageURL);
            this.inputFile.value = "";
          });
        } else {
          alert("Please upload only JPG or PNG image.");
        }
      };
    }
  };

  onClickVideo = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    const src = window.prompt("Enter the URL of the YouTube/Vimeo video:");
    if (!src) return;

    const val = this.state.value.toJSON().document!.nodes;
    const isEmpty = this.displayedValueEquals(val, PLACEHOLDER_TEXT);
    if (isEmpty) {
      this.editorRef.current?.deleteLineForward();
      this.editorRef.current?.deleteLineBackward();
      this.editorRef.current?.focus();
    }

    let URL = null;
    if (
      src.includes("https://www.youtube.com/") ||
      src.includes("https://youtu.be/")
    ) {
      const videoID = getYoutubeVideoId(src);
      URL = videoID ? `https://www.youtube.com/embed/${videoID}` : null;
    } else if (src.includes("vimeo.com/")) {
      const videoID = getVimeoVideoId(src);
      URL = videoID ? `https://player.vimeo.com/video/${videoID}` : null;
    }

    if (URL && this.editorRef.current) {
      this.editorRef.current.command(this.insertVideo, URL);
    } else {
      alert("URL should be YouTube/Vimeo video.");
    }
  };

  onClickImage = () => {
    this.inputFile.click();
  };

  onClickLink = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    const val = this.state.value.toJSON().document!.nodes;
    const isEmpty = this.displayedValueEquals(val, PLACEHOLDER_TEXT);
    if (isEmpty) {
      this.editorRef.current?.deleteLineForward();
      this.editorRef.current?.deleteLineBackward();
      this.editorRef.current?.focus();
    }

    const hasLinks = this.hasLinks();
    if (hasLinks && this.editorRef.current) {
      this.editorRef.current.command(this.unwrapLink);
    } else {
      const href = window.prompt("Enter the URL of the link:");
      const text =
        href && isUrl(href)
          ? window.prompt("Enter the text for the link:")
          : null;
      if (href && text && this.editorRef.current) {
        this.editorRef.current
          .insertText(text)
          .moveFocusBackward(text.length)
          .command(this.wrapLink, displayURL(href));
      }
    }
  };

  hasLinks = () => {
    return this.state.value.inlines.some(
      (inline: { type: string }) => inline.type === "link"
    );
  };

  wrapLink = (
    change: { wrapInline: Function; moveToEnd: Function },
    href: string
  ) => {
    change.wrapInline({
      type: "link",
      data: { href },
    });

    change.moveToEnd();
  };

  unwrapLink = (change: Controller) => {
    change.unwrapInline("link");
  };

  displayedValueEquals = (val: Any, valueToCompare = "") => {
    if (
      val.length === 1 &&
      val[0].nodes.length === 1 &&
      val[0].nodes[0].text.trim() === valueToCompare &&
      Object.keys(val[0].data).length === 0
    ) {
      return true;
    }

    return false;
  };

  setEditorEmpty = () => {
    this.setState({ value: initialValue, isEmpty: true });
  };

  insertImage = (editor: Controller, src: string, target: RangeProperties) => {
    if (target) editor.select(target);

    editor
      .insertBlock({
        type: "image",
        data: { src },
      })
      .insertBlock("paragraph");
  };

  insertVideo = (editor: Controller, src: string, target: RangeProperties) => {
    if (target) editor.select(target);

    editor
      .insertBlock({
        type: "video",
        data: { src },
      })
      .insertBlock("paragraph");
  };

  renderInline = (
    props: RenderInlineProps,
    editor: Controller,
    next: () => void
  ) => {
    const { attributes, children, node } = props;

    switch (node.type) {
      case "link": {
        const { data } = node;
        return (
          <Link href={data.get("href")} children={children} {...attributes} />
        );
      }

      default:
        return next();
    }
  };

  renderBlock = (
    props: RenderBlockProps,
    editor: SlateEditor,
    next: () => void
  ) => {
    const { attributes, node, isFocused } = props;

    switch (node.type) {
      case "image":
        return !this.props.disableImage ? (
          <Image
            src={node.data.get("src")}
            selected={isFocused}
            {...attributes}
          />
        ) : (
          next()
        );

      case "video":
        return !this.props.disableVideo ? (
          <Video
            src={node.data.get("src")}
            selected={isFocused}
            {...attributes}
          />
        ) : (
          next()
        );

      default:
        return next();
    }
  };

  onDropOrPaste = (
    event: Event,
    editor: SlateEditor & Editor,
    next: () => void
  ) => {
    const target = editor.findEventRange(event);

    if (!target && event.type === "drop") return next();

    const transfer: Any = getEventTransfer(event);
    const { type, text, files } = transfer;

    if (!this.props.disableImage && type === "files") {
      for (const file of files) {
        const reader = new FileReader();
        if (!allowedImageTypes.includes(file.type)) continue;

        reader.addEventListener("load", () => {
          this.uploadImage(reader.result).then((imageURL) => {
            editor.command(this.insertImage, imageURL, target);
          });
        });

        reader.readAsDataURL(file);
      }
    }

    if (type === "text") {
      if (!isUrl(text)) return next();
      if (!isImage(text)) return next();

      editor.command(this.insertImage, text, target);
    }

    return next();
  };

  uploadImage = (src: string | null | ArrayBuffer) => {
    this.setState({ loading: true });

    const data = {
      image: {
        file: src,
        type: "post",
      },
    };

    return new Promise((resolve) => {
      BttfAxios.post("/api/images", data, (res) => {
        this.setState({
          loading: false,
          imagesId: [
            ...this.state.imagesId,
            { id: res.data.id, name: res.data.file_url },
          ],
        });

        resolve(res.data.file_url);
      });
    });
  };

  render() {
    const className = [
      style.textEditor,
      this.state.isFocused ? style.textEditorFocus : null,
      !this.state.isEmpty || this.state.isFocused
        ? style.textEditorNonempty
        : null,
    ].join(" ");

    return (
      <ErrorBoundary instance={appsignal}>
        <div className={className}>
          <div className={style.textEditorHolder}>
            {this.state.loading && <Loader fullWidth={true} />}

            {this.withoutEditor
              ? newLineToParagraph(this.props.read || "", this.props.isDeleted)
              : (!this.props.disabledForAndroid || this.props.read) && (
                  <Editor
                    ref={this.editorRef}
                    value={this.state.value}
                    className={
                      !this.props.read
                        ? style.textEditorMain
                        : style.textEditorRead
                    }
                    onChange={this.onChange}
                    onFocus={this.onFocus}
                    onBlur={this.onBlur}
                    onDrop={this.onDropOrPaste}
                    onPaste={this.onDropOrPaste}
                    renderInline={this.renderInline}
                    renderBlock={this.renderBlock}
                    schema={initialValue}
                    spellCheck={!this.props.read}
                  />
                )}
            {!(this.props.read || this.props.disabledForAndroid) && (
              <div
                className={style.avatar}
                style={{ backgroundImage: `url(${this.props.avatar})` }}
              />
            )}
          </div>
          {!this.props.disabledForAndroid && this.props.isOwner && (
            <div
              className={[elemsStyle.customCheckbox, style.checkboxHolder].join(
                " "
              )}
            >
              <input
                type="checkbox"
                checked={!!this.props.isCompanyUpdate}
                id="asCompanyUpdate"
                onChange={this.props.setCompanyUpdate}
              />
              <label htmlFor="asCompanyUpdate">Company update</label>
            </div>
          )}
          {!this.props.disabledForAndroid && !this.props.read && (
            <div className={style.textEditorToolbar}>
              {!this.props.disableImage && (
                <button
                  className={style.uploadImageBtn}
                  onClick={this.onClickImage}
                >
                  <span className="fa fa-camera" />
                  Image
                  <input
                    type="file"
                    onChange={this.onImageUpload}
                    className={style.fileInput}
                    ref={(input) =>
                      (this.inputFile = input as HTMLInputElement)
                    }
                  />
                </button>
              )}
              {!this.props.disableVideo && (
                <button
                  className={style.uploadImageBtn}
                  onClick={this.onClickVideo}
                >
                  <span className="fa fa-video-camera" />
                  Video
                </button>
              )}
              <button
                className={style.uploadImageBtn}
                onClick={this.onClickLink}
              >
                <span className="fa fa-link" />
                Link
              </button>
            </div>
          )}
        </div>
      </ErrorBoundary>
    );
  }
}
